import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import HubSpotEmbedForm, { IHubSpotEmbedFormProps } from '../HubSpotEmbedForm';
import AnimatedArcs from '../../common/AnimatedArcs';

export interface IHubSpotFormSectionProps extends IBaseElementProps {
	heading?: string;
	description?: string;
	hubSpotEmbedForm?: IHubSpotEmbedFormProps;
	compactLayout?: boolean;
	/**
	 * Line Animation Delay in Milliseconds
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels
	 */
	lineAnimationOffset?: number;
}

/**
 * HubSpot Form Section
 * @icon list-view
 * @block
 */
const HubSpotFormSection: React.FC<IHubSpotFormSectionProps> = ({
	id,
	className,
	classNames = [],
	heading,
	description,
	hubSpotEmbedForm,
	compactLayout = false,
	lineAnimationDelay = 1000,
	lineAnimationOffset = 150,
}) => {
	const ref = useRef(null);

	useEffect(() => {
		function moveSection() {
			if (!ref.current) {
				return;
			}

			const _self = ref.current;
			const parent = _self.closest('[data-component]');
			const form = _self.querySelector('.hs-form__target');

			if (window.innerWidth >= 992 ) {
				// Check the opacity equals 1 meaning the form height is set
				if (_self.offsetHeight < parent.offsetHeight && form.style.opacity === '1') {
					// This sets an interval of the scroll distance
					// It keeps the scroll distance in-between the height of the parent height
					const distance =
						document.documentElement.scrollTop +
							_self.offsetHeight >
						parent.offsetHeight
							? parent.offsetHeight - _self.offsetHeight
							: document.documentElement.scrollTop;
					_self.style.transform = `translateY(${distance}px)`;
				}
			} else {
				_self.style.transform = '';
			}
		}

		window.addEventListener('scroll', moveSection);
		window.addEventListener('resize', moveSection);

		return () => {
			window.removeEventListener('scroll', moveSection);
			window.removeEventListener('resize', moveSection);
		};
	}, []);

	return (
		<div
			className={
				className ||
				cn(
					`hs-form-section__container ${
						compactLayout ? 'compact-layout' : ''
					}`,
					...classNames,
				)
			}
			id={id}
			ref={ref}
		>
			<div className="hs-form-section__container-inner">
				{(heading || description) && (
					<div className="hs-form-section__upper text-center">
						{heading && !compactLayout && <h2>{heading}</h2>}
						{heading && compactLayout && <h3>{heading}</h3>}
						{description && <p className="lead">{description}</p>}
					</div>
				)}
				<div className="hs-form-section__lower">
					{hubSpotEmbedForm && (
						<HubSpotEmbedForm {...hubSpotEmbedForm} />
					)}
				</div>
				<AnimatedArcs
					arcsStyle={'Component - Two Lines'}
					arcsPosition="Right"
					arcOneColor="var(--bs-primary)"
					arcOneStrokeWidth={3}
					arcTwoColor="var(--bs-primary)"
					arcTwoStrokeWidth={3}
					animationDelay={lineAnimationDelay}
					animationOffset={lineAnimationOffset}
				/>
			</div>
		</div>
	);
};

export default HubSpotFormSection;
